/**
 * ToolActivity — collapsible trail of the agent's server-side stages while a
 * run is in flight (Planning → Gathering Data → Executing Tools …). The stage
 * label comes from the polled chat status; the trail accumulates the distinct
 * stages seen during the current run and resets when the run completes.
 */
import React, { useEffect, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  LoaderCircleIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ToolActivityProps {
  /** Whether the agent is actively running. */
  running: boolean;
  /** Current stage label (e.g. "Gathering Data"). */
  stage: string;
  onCancel?: () => void;
}

export const useRunTrail = (running: boolean, stage: string): string[] => {
  const [trail, setTrail] = useState<string[]>([]);
  useEffect(() => {
    if (!running) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTrail([]);
      return;
    }
    const label = stage.trim();
    if (!label) return;
    setTrail((prev) =>
      prev[prev.length - 1] === label ? prev : [...prev, label]
    );
  }, [running, stage]);
  return trail;
};

const ToolActivity: React.FC<ToolActivityProps> = ({
  running,
  stage,
  onCancel,
}) => {
  const trail = useRunTrail(running, stage);
  const [expanded, setExpanded] = useState(false);

  if (!running) return null;

  const current = stage.trim() || 'Initializing';
  const history = trail.slice(0, -1);

  return (
    <section
      role="status"
      aria-live="polite"
      data-testid="ai-tool-activity"
      className="tw:flex tw:flex-col tw:gap-1.5 tw:rounded-xl tw:border tw:border-dashed tw:border-border tw:bg-muted/40 tw:px-3.5 tw:py-2.5 tw:text-sm"
    >
      <div className="tw:flex tw:items-center tw:gap-2">
        <LoaderCircleIcon className="tw:size-4 tw:shrink-0 tw:animate-spin tw:text-primary" />
        <span className="tw:font-medium">{current}</span>
        <span className="tw:ml-auto tw:flex tw:items-center tw:gap-1">
          {history.length > 0 && (
            <Button
              variant="ghost"
              size="xs"
              aria-expanded={expanded}
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              {history.length} step{history.length === 1 ? '' : 's'}
            </Button>
          )}
          {onCancel && (
            <Button
              variant="outline"
              size="xs"
              onClick={onCancel}
              aria-label="Cancel current run"
            >
              Cancel
            </Button>
          )}
        </span>
      </div>
      {expanded && history.length > 0 && (
        <ol className="tw:m-0 tw:list-none tw:space-y-0.5 tw:pl-6 tw:text-xs tw:text-muted-foreground">
          {history.map((step, i) => (
            <li key={`${step}-${i}`} className={cn('tw:opacity-80')}>
              {step}
            </li>
          ))}
        </ol>
      )}
      <p className="tw:m-0 tw:pl-6 tw:text-xs tw:text-muted-foreground">
        Responses usually take 10–60 seconds; large data sets take longer.
      </p>
    </section>
  );
};

export default ToolActivity;
